import { getApiBaseUrl } from "../config/apiBase";
import { apiFetch } from "../config/apiFetch";
import type { MilestoneItemKind, MilestoneTemplate, MilestoneTemplateItem } from "../types";

async function parseError(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const j = JSON.parse(text) as { error?: string };
    if (j.error) return j.error;
  } catch {
    /* ignore */
  }
  return text || `Request failed (${res.status})`;
}

export async function fetchMilestoneTemplates(): Promise<MilestoneTemplate[]> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/milestone-templates`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<MilestoneTemplate[]>;
}

export async function createMilestoneTemplateApi(input: {
  title: string;
  notes?: string;
  items?: { kind: MilestoneItemKind; label: string }[];
  linkedConstructionTimelinePhaseIds?: string[];
  linkedResourceIds?: string[];
}): Promise<MilestoneTemplate> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/milestone-templates`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      title: input.title,
      notes: input.notes ?? "",
      items: input.items ?? [],
      linkedConstructionTimelinePhaseIds: input.linkedConstructionTimelinePhaseIds ?? [],
      linkedResourceIds: input.linkedResourceIds ?? [],
    }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<MilestoneTemplate>;
}

export async function updateMilestoneTemplateApi(
  id: string,
  patch: Partial<
    Pick<
      MilestoneTemplate,
      "title" | "notes" | "sortOrder" | "linkedConstructionTimelinePhaseIds" | "linkedResourceIds"
    >
  >,
): Promise<MilestoneTemplate> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/milestone-templates/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<MilestoneTemplate>;
}

export async function deleteMilestoneTemplateApi(id: string): Promise<void> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/milestone-templates/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!res.ok && res.status !== 204) throw new Error(await parseError(res));
}

export async function createMilestoneTemplateItemApi(
  templateId: string,
  item: { kind: MilestoneItemKind; label: string },
): Promise<MilestoneTemplateItem> {
  const res = await apiFetch(
    `${getApiBaseUrl()}/api/milestone-templates/${encodeURIComponent(templateId)}/items`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(item),
    },
  );
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<MilestoneTemplateItem>;
}

export async function deleteMilestoneTemplateItemApi(templateId: string, itemId: string): Promise<void> {
  const res = await apiFetch(
    `${getApiBaseUrl()}/api/milestone-templates/${encodeURIComponent(templateId)}/items/${encodeURIComponent(itemId)}`,
    { method: "DELETE" },
  );
  if (!res.ok && res.status !== 204) throw new Error(await parseError(res));
}
